import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import {
	IconAlertTriangle,
	IconClock,
	IconEye,
	IconGavel,
	IconShieldCheck,
} from "@tabler/icons-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convex-api";

type DisputeTypeFilter =
	| "all"
	| "delivery_timeout"
	| "dispute_raised"
	| "large_amount_review";

const DISPUTE_TYPE_LABELS: Record<string, string> = {
	delivery_timeout: "Delivery Timeout",
	dispute_raised: "Dispute Raised",
	large_amount_review: "Large Amount Review",
};

export const Route = createFileRoute("/_authenticated/disputes/")({
	component: DisputesPage,
});

function DisputesPage() {
	const [typeFilter, setTypeFilter] = useState<DisputeTypeFilter>("all");

	const stats = useQuery(api.disputes.getStats);
	const unresolvedData = useQuery(api.disputes.getUnresolved, {
		disputeType: typeFilter === "all" ? undefined : typeFilter,
		limit: 50,
		offset: 0,
	});

	const isLoadingStats = stats === undefined;
	const isLoadingDisputes = unresolvedData === undefined;

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-8">
				<h1 className="font-bold text-3xl">Dispute Resolution</h1>
				<p className="mt-1 text-muted-foreground text-sm">
					Review and resolve errand disputes.
				</p>
			</div>

			{/* Stats Cards */}
			<div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
				{isLoadingStats ? (
					Array.from({ length: 4 }).map((_, i) => (
						<Skeleton className="h-24" key={`stat-skeleton-${String(i)}`} />
					))
				) : stats ? (
					<>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between pb-2">
								<CardTitle className="font-medium text-sm">
									Unresolved
								</CardTitle>
								<IconAlertTriangle className="h-4 w-4 text-orange-500" />
							</CardHeader>
							<CardContent>
								<div className="font-bold text-2xl">{stats.unresolved}</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between pb-2">
								<CardTitle className="font-medium text-sm">
									Delivery Timeouts
								</CardTitle>
								<IconClock className="h-4 w-4 text-yellow-500" />
							</CardHeader>
							<CardContent>
								<div className="font-bold text-2xl">
									{stats.byType.delivery_timeout}
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between pb-2">
								<CardTitle className="font-medium text-sm">
									User Disputes
								</CardTitle>
								<IconGavel className="h-4 w-4 text-red-500" />
							</CardHeader>
							<CardContent>
								<div className="font-bold text-2xl">
									{stats.byType.dispute_raised}
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between pb-2">
								<CardTitle className="font-medium text-sm">
									Resolved Total
								</CardTitle>
								<IconShieldCheck className="h-4 w-4 text-green-500" />
							</CardHeader>
							<CardContent>
								<div className="font-bold text-2xl">{stats.resolved}</div>
							</CardContent>
						</Card>
					</>
				) : null}
			</div>

			{/* Filter */}
			<div className="mb-6 flex items-center justify-between">
				<h2 className="font-semibold text-lg">Unresolved Disputes</h2>
				<Select
					onValueChange={(value) => setTypeFilter(value as DisputeTypeFilter)}
					value={typeFilter}
				>
					<SelectTrigger className="w-[220px]">
						<SelectValue placeholder="Filter by type" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Types</SelectItem>
						<SelectItem value="delivery_timeout">Delivery Timeout</SelectItem>
						<SelectItem value="dispute_raised">Dispute Raised</SelectItem>
						<SelectItem value="large_amount_review">
							Large Amount Review
						</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{/* Table */}
			{isLoadingDisputes ? (
				<div className="space-y-3">
					{Array.from({ length: 5 }).map((_, i) => (
						<Skeleton
							className="h-12 w-full"
							key={`row-skeleton-${String(i)}`}
						/>
					))}
				</div>
			) : !unresolvedData || unresolvedData.disputes.length === 0 ? (
				<div className="flex items-center justify-center rounded-md border py-10">
					<p className="text-muted-foreground">No unresolved disputes.</p>
				</div>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Type</TableHead>
								<TableHead>Errand</TableHead>
								<TableHead>Reason</TableHead>
								<TableHead>Created</TableHead>
								<TableHead>Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{unresolvedData.disputes.map(
								(dispute: (typeof unresolvedData.disputes)[number]) => (
									<TableRow key={dispute._id}>
										<TableCell>
											<Badge variant="secondary">
												{DISPUTE_TYPE_LABELS[dispute.disputeType] ??
													dispute.disputeType}
											</Badge>
										</TableCell>
										<TableCell>
											{dispute.errand ? (
												<Link
													to="/errands/$errandId"
													params={{ errandId: dispute.errand.id }}
													className="font-medium text-blue-600 underline-offset-2 hover:underline"
												>
													{dispute.errand.title}
												</Link>
											) : (
												<span className="text-muted-foreground">Unknown</span>
											)}
										</TableCell>
										<TableCell className="max-w-[250px] truncate">
											{dispute.reason ?? "No reason provided"}
										</TableCell>
										<TableCell>
											{new Date(dispute.createdAt).toLocaleDateString()}
										</TableCell>
										<TableCell>
											<Button asChild size="sm" variant="outline">
												<Link
													to="/disputes/$disputeId"
													params={{ disputeId: dispute._id }}
												>
													<IconEye className="mr-2 h-4 w-4" />
													Resolve
												</Link>
											</Button>
										</TableCell>
									</TableRow>
								),
							)}
						</TableBody>
					</Table>
					<div className="border-t px-4 py-3 text-muted-foreground text-sm">
						Showing {unresolvedData.disputes.length} of {unresolvedData.total}{" "}
						disputes
					</div>
				</div>
			)}
		</div>
	);
}
